import { useState } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  ATAJOS,
  CUALQUIER_FECHA,
  atajoDelRango,
  cerrarRango,
  diaDeBolivia,
  diaLargo,
  diasDelMes,
  elegirDia,
  estaEnElRango,
  nombreDelMes,
  pieDelRango,
  rangoDelAtajo,
  textoDelRango,
  type Atajo,
  type RangoDeFechas,
} from '../lib/rango-de-fechas';

/**
 * El filtro de fechas con calendario (lote B2, spec 246 punto 7; maqueta
 * #b2-fechas): atajos a la izquierda, dos meses, y al pie qué cubre en hora de
 * Bolivia. Se toca un día y otro y el rango queda pintado; «Aplicar» lo pone.
 * Lo usan Estrategias (creada), Aprobadas y Descartadas (decidida) y el historial.
 */

const SEMANA = ['lu', 'ma', 'mi', 'ju', 'vi', 'sá', 'do'];

function Mes({
  anio,
  mes0,
  rango,
  onElegir,
}: {
  anio: number;
  mes0: number;
  rango: RangoDeFechas;
  onElegir: (dia: string) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
      {SEMANA.map((d) => (
        <span key={d} className="py-1 text-muted-foreground">
          {d}
        </span>
      ))}
      {diasDelMes(anio, mes0).map((dia, i) =>
        dia == null ? (
          <span key={`vacio-${i}`} />
        ) : (
          <button
            key={dia}
            type="button"
            aria-label={diaLargo(dia)}
            aria-pressed={dia === rango.desde || dia === rango.hasta}
            data-en-rango={estaEnElRango(rango, dia) || undefined}
            onClick={() => onElegir(dia)}
            className={cn(
              'h-8 rounded-md tabular-nums hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              estaEnElRango(rango, dia) && 'bg-primary/10',
              (dia === rango.desde || dia === rango.hasta) && 'bg-primary text-primary-foreground'
            )}
          >
            {Number(dia.slice(8))}
          </button>
        )
      )}
    </div>
  );
}

export function SelectorDeFechas({
  etiqueta,
  valor,
  onCambiar,
  hoy = diaDeBolivia(),
}: {
  /** «Creada», «Aprobada», «Descartada», «Cambiado». */
  etiqueta: string;
  valor: RangoDeFechas;
  onCambiar: (r: RangoDeFechas) => void;
  /** Para los tests: el día de hoy en Bolivia. */
  hoy?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [borrador, setBorrador] = useState<RangoDeFechas>(valor);
  const [atajo, setAtajo] = useState<Atajo>(atajoDelRango(valor, hoy));
  // El mes de la izquierda: el del primer día elegido, o el anterior al de hoy.
  const mesDe = (desde: string | null) => {
    const base = desde ?? hoy;
    const d = new Date(`${base.slice(0, 7)}-15T12:00:00Z`);
    if (!desde) d.setUTCMonth(d.getUTCMonth() - 1);
    return { anio: d.getUTCFullYear(), mes0: d.getUTCMonth() };
  };
  const [mes, setMes] = useState(() => mesDe(valor.desde));
  const siguiente = new Date(Date.UTC(mes.anio, mes.mes0 + 1, 15));
  const mover = (n: number) => {
    const d = new Date(Date.UTC(mes.anio, mes.mes0 + n, 15));
    setMes({ anio: d.getUTCFullYear(), mes0: d.getUTCMonth() });
  };

  const abrir = (v: boolean) => {
    setAbierto(v);
    if (v) {
      setBorrador(valor);
      setAtajo(atajoDelRango(valor, hoy));
      // Ronda final B2: al reabrirlo, el mes del rango de ahora (no el del primer montaje).
      if (valor.desde) setMes(mesDe(valor.desde));
    }
  };
  const tocarAtajo = (a: Atajo) => {
    setAtajo(a);
    const r = rangoDelAtajo(a, hoy);
    if (r) setBorrador(r);
  };
  const elegido = valor.desde != null || valor.hasta != null;

  return (
    <Popover open={abierto} onOpenChange={abrir}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${etiqueta}: ${textoDelRango(valor, hoy)}`}
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            elegido && 'border-primary'
          )}
        >
          <CalendarDays aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          <span>
            {etiqueta}:{' '}
            <span className={cn(elegido && 'font-semibold text-primary')}>
              {textoDelRango(valor, hoy)}
            </span>
          </span>
          <ChevronDown aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <div className="flex">
          <div role="group" aria-label="Atajos" className="flex w-48 flex-col gap-0.5 border-r p-2">
            {ATAJOS.map((a) => (
              <button
                key={a.clave}
                type="button"
                aria-pressed={atajo === a.clave}
                onClick={() => tocarAtajo(a.clave)}
                className={cn(
                  'rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                  atajo === a.clave && 'bg-primary/10 font-medium text-primary'
                )}
              >
                {a.texto}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4 p-3">
            {[
              { anio: mes.anio, mes0: mes.mes0, flecha: 'ANTERIOR' as const },
              {
                anio: siguiente.getUTCFullYear(),
                mes0: siguiente.getUTCMonth(),
                flecha: 'SIGUIENTE' as const,
              },
            ].map((m) => (
              <div key={`${m.anio}-${m.mes0}`} className="w-56 space-y-1">
                <div className="flex items-center justify-between text-sm font-medium">
                  {m.flecha === 'ANTERIOR' ? (
                    <button
                      type="button"
                      aria-label="Mes anterior"
                      onClick={() => mover(-1)}
                      className="rounded-md p-1 hover:bg-accent"
                    >
                      <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="w-6" />
                  )}
                  <span>{nombreDelMes(m.anio, m.mes0)}</span>
                  {m.flecha === 'SIGUIENTE' ? (
                    <button
                      type="button"
                      aria-label="Mes siguiente"
                      onClick={() => mover(1)}
                      className="rounded-md p-1 hover:bg-accent"
                    >
                      <ChevronRight aria-hidden="true" className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="w-6" />
                  )}
                </div>
                <Mes
                  anio={m.anio}
                  mes0={m.mes0}
                  rango={borrador}
                  onElegir={(dia) => {
                    setAtajo('CALENDARIO');
                    setBorrador((r) => elegirDia(r, dia));
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t px-3 py-2">
          <span
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid="pie-del-calendario"
          >
            <Clock aria-hidden="true" className="h-3.5 w-3.5" />
            {pieDelRango(cerrarRango(borrador))}
          </span>
          <span className="flex gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBorrador(CUALQUIER_FECHA);
                setAtajo('CUALQUIERA');
              }}
            >
              Limpiar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onCambiar(cerrarRango(borrador));
                setAbierto(false);
              }}
            >
              Aplicar
            </Button>
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
